import type { ComponentType } from 'react';

import type { DynamicOptions, LoaderComponent } from 'next/dynamic';
import dynamic from 'next/dynamic';

import { ChunkSkewErrorBoundary } from 'components/ChunkSkewErrorBoundary';

import { importWithRetry } from './importWithRetry';

export function dynamicWithRetry<P extends object>(
    importFn: () => LoaderComponent<P>,
    options: DynamicOptions<P> = {},
): ComponentType<P> {
    const loader = () => importWithRetry(importFn);

    const DynamicComponent =
        options.loading !== undefined
            ? dynamic<P>(loader, {
                  loading: options.loading,
                  ssr: options.ssr ?? false,
              })
            : dynamic<P>(loader, {
                  ssr: options.ssr ?? false,
              });

    // A chunk from a newer deploy can load successfully but throw at render
    // time when it references exports missing from the older shared modules
    // already in memory. importWithRetry only covers chunks that fail to
    // load, so wrap every lazy component in the skew boundary to recover.
    return function DynamicWithSkewRecovery(props: P) {
        return (
            <ChunkSkewErrorBoundary>
                <DynamicComponent {...props} />
            </ChunkSkewErrorBoundary>
        );
    };
}
